import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { TgEmoji } from "@/components/TgEmoji";
import { PopupCloseButton } from "@/components/PopupClose";
import { ActionButton } from "@/components/ActionButton";
import { Icon } from "@/components/Icon";
import { useBottomInset, useTopInset } from "@/lib/useTopInset";
import { WheelDoubleSkipPopup } from "@/components/WheelDoubleSkipPopup";
import { markWheelDouble, useSubmitWheelDouble, useWheelDouble, type WheelDoubleState } from "@/api/hooks";
import { tg } from "@/lib/telegram";
import { errorText } from "@/components/LockedPage";

/**
 * «Подвоїти приз» (owner 2026-10-25; з 04.11.2026 — за підписку на Instagram inFLOW замість поста в Threads).
 * Лише в моменті: без таймера; закрив вікно → «Точно не подвоювати?» → «Пропустити» = можливість зникла.
 * Два кроки: підписатись на @inflow.in.ua і надіслати свою сторінку. Далі — як «тиждень за Instagram засновника»:
 * заявка в канал, перевірка підписки через 12 годин, відповідь у бот (`services/wheel_double.py`).
 */

const DEMO: WheelDoubleState = {
  eligible: true,
  status: "open",
  prize: "3 дні підписки",
  doubled: "6 днів підписки",
  brand_handle: "inflow.in.ua",
  brand_url: "https://instagram.com/inflow.in.ua",
};

export function WheelDoublePopup({
  spinId,
  onClose,
  demo,
  demoResult,
}: {
  spinId: number | null;
  /** `declined` — людина підтвердила «Пропустити». */
  onClose: (declined?: boolean) => void;
  /** Галерея попапів: без сервера. */
  demo?: boolean;
  /** Галерея: одразу показати «заявку прийнято», помилку, повторну спробу чи питання «Точно не подвоювати?». */
  demoResult?: "sent" | "error" | "retry" | "skip";
}) {
  const q = useWheelDouble(demo ? null : spinId);
  const submit = useSubmitWheelDouble(spinId);
  const st: WheelDoubleState | undefined = demo
    ? demoResult === "retry"
      ? { ...DEMO, retry: true, ig_url: "https://instagram.com/anna.smm" }
      : DEMO
    : q.data;
  const [url, setUrl] = useState("");
  const prefilled = useRef(false);
  const [err, setErr] = useState<string | null>(
    demoResult === "error" ? "Це сторінка inFLOW. Встав посилання на свою сторінку в Instagram." : null,
  );
  const [sent, setSent] = useState(demoResult === "sent");
  const [askSkip, setAskSkip] = useState(demoResult === "skip");
  const [skipping, setSkipping] = useState(false);
  const marked = useRef(false);
  // Не на весь екран (owner 2026-10-26): над вікном — кнопки Telegram («Закрыть», «…»), хрестик ховався під ними.
  const topInset = useTopInset();
  const bottomInset = useBottomInset();

  // Сторінку, яку людина вже надсилала, підставляємо — наступного місяця не треба вставляти знову (owner 2026-11-04).
  useEffect(() => {
    if (prefilled.current || !st?.ig_url) return;
    prefilled.current = true;
    setUrl(st.ig_url);
  }, [st?.ig_url]);

  // Відкрив подвоєння — сервер памʼятає: якщо застосунок перезапуститься, поки людина в Instagram, вікно відкриється знову.
  useEffect(() => {
    if (demo || spinId === null || marked.current || st?.status !== "open") return;
    marked.current = true;
    void markWheelDouble(spinId, "started").catch(() => undefined);
  }, [demo, spinId, st?.status]);

  const isOpen = !sent && st?.status === "open";

  const tryClose = () => {
    if (!isOpen) onClose();
    else setAskSkip(true);
  };

  const skip = async () => {
    setSkipping(true);
    try {
      if (!demo && spinId !== null) await markWheelDouble(spinId, "declined");
    } catch {
      /* не записалось — однаково закриваємо: людина свій вибір зробила */
    }
    setSkipping(false);
    setAskSkip(false);
    onClose(true);
  };

  const openInstagram = () => {
    if (!demo && spinId !== null) void markWheelDouble(spinId, "insta").catch(() => undefined);
    const link = st?.brand_url ?? "https://instagram.com/inflow.in.ua";
    tg()?.HapticFeedback?.impactOccurred?.("light");
    const w = tg();
    if (w?.openLink) w.openLink(link);
    else window.open(link, "_blank");
  };

  const send = async () => {
    if (!url.trim() || submit.isPending) return;
    setErr(null);
    if (demo) {
      setSent(true);
      return;
    }
    try {
      await submit.mutateAsync(url.trim());
      tg()?.HapticFeedback?.notificationOccurred?.("success");
      setSent(true);
    } catch (e) {
      tg()?.HapticFeedback?.notificationOccurred?.("error");
      setErr(errorText(e, "Заявка не відправилась. Спробуй ще раз за хвилину."));
    }
  };

  let content: ReactNode;
  let footer: ReactNode = null;
  if (sent || st?.status === "pending") {
    content = (
      <div className="fade-in text-center">
        <div className="mb-2 flex justify-center">
          <TgEmoji char="⏳" size={52} />
        </div>
        <div className="mb-1.5 font-display text-[1.1875rem] font-bold leading-tight text-ink [text-wrap:balance]">
          Заявку прийнято
        </div>
        <div className="text-[0.84375rem] leading-relaxed text-ink-dim [text-wrap:balance]">
          Перевіримо підписку й подвоїмо приз до{" "}
          <b className="whitespace-nowrap text-ink">{st?.doubled}</b>. Напишемо тобі в бот.
        </div>
      </div>
    );
    footer = <ActionButton onClick={() => onClose()}>Зрозуміло</ActionButton>;
  } else if (!st) {
    content = <div className="py-10 text-center text-[0.8125rem] text-ink-dim">Завантаження…</div>;
  } else if (st.status !== "open") {
    content = (
      <div className="text-center">
        <div className="mb-2 flex justify-center">
          <TgEmoji char={st.status === "doubled" ? "✅" : "⏳"} size={52} />
        </div>
        <div className="mb-1.5 font-display text-[1.1875rem] font-bold leading-tight text-ink [text-wrap:balance]">
          {st.status === "doubled" ? "Приз уже подвоєно" : "Подвоєння вже недоступне"}
        </div>
        <div className="text-[0.84375rem] leading-relaxed text-ink-dim [text-wrap:balance]">
          {st.status === "doubled"
            ? "Подвоєна частина вже нарахована."
            : "Подвоїти приз можна лише одразу після виграшу."}
        </div>
      </div>
    );
    footer = <ActionButton onClick={() => onClose()}>Зрозуміло</ActionButton>;
  } else {
    const handle = st.brand_handle ?? "inflow.in.ua";
    content = (
      <>
        <div className="text-center">
          <div className="mb-2 flex justify-center">
            <TgEmoji char="🚀" size={36} />
          </div>
          <div className="mb-1.5 font-display text-[1.1875rem] font-bold leading-tight text-ink [text-wrap:balance]">
            Подвій приз за підписку
          </div>
          <div className="text-[0.8125rem] leading-relaxed text-ink-dim [text-wrap:balance]">
            {st.retry
              ? "Минулого разу не побачили підписки. Підпишись на Instagram inFLOW і надішли сторінку ще раз."
              : "Підпишись на Instagram inFLOW — і приз подвоїться."}
          </div>
          {/* «3 дні підписки → 6 днів підписки» на 375 px не влазить в один рядок — переноситься, а не вилазить. */}
          <div className="mt-2 flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 font-display text-[0.875rem] font-bold">
            <span className="whitespace-nowrap text-ink-dim line-through decoration-1">{st.prize}</span>
            <Icon name="arrow-right" size={14} className="shrink-0 text-primary" />
            <span className="whitespace-nowrap text-primary">{st.doubled}</span>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          <Step n={1} title={`Підпишись на @${handle}`} hint="Там корисний і цікавий контент.">
            <button
              type="button"
              onClick={openInstagram}
              className="mt-2.5 flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-ink py-2.5 font-display text-[0.8125rem] font-semibold text-white active:bg-ink-2"
            >
              <Icon name="instagram" size={14} color="#fff" />
              Відкрити Instagram
            </button>
          </Step>

          <Step n={2} title="Надішли свою сторінку" hint="Посилання на профіль або просто нік.">
            <input
              type="url"
              inputMode="url"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder="instagram.com/твійнік"
              value={url}
              maxLength={300}
              onChange={(e) => {
                setUrl(e.target.value);
                setErr(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") void send();
              }}
              className={
                "mt-2.5 w-full rounded-xl bg-surface px-3 py-2.5 text-[0.8125rem] text-ink outline-none ring-1 transition " +
                "placeholder:text-ink-faint " +
                (err ? "ring-red-400" : "ring-ink/10 focus:ring-primary/40")
              }
            />
            {err && <div className="mt-1.5 text-[0.71875rem] leading-snug text-red-500 [text-wrap:balance]">{err}</div>}
          </Step>
        </div>
      </>
    );
    footer = (
      <ActionButton disabled={!url.trim() || submit.isPending} onClick={send}>
        {submit.isPending ? "Надсилаємо…" : "Надіслати на перевірку"}
      </ActionButton>
    );
  }

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
        // Питання «Точно не подвоювати?» показуємо замість вікна, а не поверх (owner 2026-10-26); вікно лише
        // ховаємо — введена сторінка лишається, якщо людина повернеться.
        style={{ paddingTop: topInset + 16, paddingBottom: bottomInset + 16, visibility: askSkip ? "hidden" : undefined }}
        onClick={tryClose}
      >
        <div
          className="relative flex max-h-[min(74dvh,100%)] w-full max-w-sm flex-col overflow-hidden rounded-3xl bg-surface shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {isOpen && <PopupCloseButton onClose={tryClose} />}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-3 pt-5">{content}</div>
          {footer && <div className="border-t border-line bg-surface px-5 pb-5 pt-3">{footer}</div>}
        </div>
      </div>
      {askSkip && st && (
        <WheelDoubleSkipPopup
          prize={st.prize ?? ""}
          doubled={st.doubled ?? ""}
          busy={skipping}
          onDouble={() => setAskSkip(false)}
          onSkip={skip}
        />
      )}
    </>,
    document.body,
  );
}

function Step({ n, title, hint, children }: { n: number; title: string; hint: string; children: ReactNode }) {
  return (
    <div className="rounded-2xl bg-bg/60 p-3">
      <div className="flex items-start gap-3">
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary font-display text-[0.8125rem] font-bold text-white">
          {n}
        </span>
        <div className="min-w-0">
          <div className="font-display text-[0.875rem] font-bold text-ink [overflow-wrap:anywhere]">{title}</div>
          <div className="text-[0.71875rem] leading-snug text-ink-dim [text-wrap:balance]">{hint}</div>
        </div>
      </div>
      {children}
    </div>
  );
}
